// Ported from the native app's MarketData.swift.
//
// Since D-022 this module is the stable facade over the data-provider
// layer (src/lib/data-providers/): callers keep the same functions and
// range/interval vocabulary; where the data comes from — the free
// aggregate or the user's own connected provider — is routed underneath.

export interface BollingerRow {
  label: string;
  bottom: number;
  top: number;
}

export interface TickerSnapshot {
  symbol: string;
  currentPrice?: number;
  /** Percent change vs the previous daily close, when derivable. */
  changePercent?: number;
  rows: BollingerRow[];
  errorMessage?: string;
}

/**
 * Classic Bollinger Band: 20-period SMA +/- 2 standard deviations, computed
 * over whatever closes are available if fewer than 20.
 */
export function bollingerBand(closes: number[], period = 20): { lower: number; upper: number } | null {
  if (closes.length < 2) return null;
  const window = closes.slice(-period);
  const n = window.length;
  const mean = window.reduce((a, b) => a + b, 0) / n;
  const variance = window.reduce((a, b) => a + (b - mean) * (b - mean), 0) / n;
  const stddev = Math.sqrt(variance);
  return { lower: mean - 2 * stddev, upper: mean + 2 * stddev };
}

export class MarketDataError extends Error {}

export interface OHLCPoint {
  date: Date;
  open: number;
  high: number;
  low: number;
  close: number;
  /** 0 when the source doesn't report volume for this bar. */
  volume: number;
}

// Imported after the type/class declarations above because the provider
// modules import OHLCPoint/MarketDataError from this file (cycle is
// type-only plus the error class, which is hoisted safely).
import { routedCandles, routedCloses } from "./data-providers/registry.js";

/**
 * OHLC candles for charts, across whichever range/interval is requested
 * (weekly/daily/hourly/30m) — served by the active data provider.
 */
export async function fetchCandles(symbol: string, range: string, interval: string): Promise<OHLCPoint[]> {
  const { candles } = await routedCandles(symbol, range, interval);
  return candles;
}

/** Close prices (and the current price) for a range/interval. The name is
 * historical — data now comes from the active provider, not only Yahoo. */
export async function fetchYahooCloses(
  symbol: string,
  range: string,
  interval: string
): Promise<{ closes: number[]; regularMarketPrice?: number }> {
  return await routedCloses(symbol, range, interval);
}

/**
 * LITE build (D-159): one ticker's snapshot — current price + day-change —
 * from a single backend-cached daily series (the backend polls Yahoo hourly;
 * clients never call Yahoo). The watchlist card shows price + change; the Build
 * page's Bollinger readings come from confluence-data's per-timeframe fetches,
 * so the snapshot no longer computes the old multi-timeframe band rows.
 */
export async function loadSnapshot(symbol: string): Promise<TickerSnapshot> {
  const snapshot: TickerSnapshot = { symbol, rows: [] };
  try {
    const daily = await fetchYahooCloses(symbol, "1y", "1d");
    const closes = daily.closes;
    snapshot.currentPrice = daily.regularMarketPrice ?? (closes.length ? closes[closes.length - 1] : undefined);
    const prevClose = closes.length >= 2 ? closes[closes.length - 2] : undefined;
    if (snapshot.currentPrice != null && prevClose != null && prevClose !== 0) {
      snapshot.changePercent = ((snapshot.currentPrice - prevClose) / prevClose) * 100;
    }
    const bb = bollingerBand(closes);
    if (bb) snapshot.rows = [{ label: "1D", bottom: bb.lower, top: bb.upper }];
    if (snapshot.currentPrice == null && !closes.length) snapshot.errorMessage = "No data";
  } catch {
    snapshot.errorMessage = "Unavailable";
  }
  return snapshot;
}

export interface PriceAlert {
  symbol: string;
  targetPrice: number;
  /** Which side of the target price was current when the alert was set (or last checked). */
  wasAbove: boolean;
}
